import axios from 'axios';
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as Yup from 'yup';
import { ForgetPasswordModal } from '../modals/ForgetPasswordModal';

import { Button, CircularProgress } from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import ReCAPTCHA from 'react-google-recaptcha';
import kixieLogo from '../../../images/kixie-logo-dark.svg';
import '../../../_metronic/_assets/sass/pages/login/classic/login-4.scss';
/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const useStyles = makeStyles({
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
});

function Login({ intl, handleLogin }) {
  const classes = useStyles();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [info] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        })
      ),
    password: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        })
      ),
  });
  const initialValues = useRef({
    email: '',
    password: '',
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return 'is-invalid';
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return 'is-valid';
    }

    return '';
  };
  const resetInitialState = () => {
    initialValues.current.email = '';
    initialValues.current.password = '';
  };

  type LoginValues = {
    email: string;
    password: string;
    captchaToken?: string;
  };

  const formik = useFormik<LoginValues>({
    initialValues: initialValues.current,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      let creds = { ...values };
      let token: string | null | undefined = null;
      if (captchaRequired) {
        token = await recaptchaRef.current?.getValue();
        if (!token) {
          return;
        }
        creds = { ...values, captchaToken: token };
      }
      const resp = await handleLogin(
        creds,
        setStatus,
        setSubmitting,
        intl,
        resetInitialState
      );
      if (resp.success) {
        setStatus(null);
      }
      if (!resp.success && resp?.error?.response?.data?.captchaRequired) {
        if (!token) {
          setStatus(
            intl.formatMessage({
              id: 'AUTH.VALIDATION.REQUIRES_CAPTCHA',
            })
          );
        }
        setCaptchaRequired(true);
        recaptchaRef.current?.reset(); // Reset only if CAPTCHA is still required
      } else {
        setCaptchaRequired(false); // Successful login
      }
    },
  });

  // const handleForgetPasswordModal = (message) => {
  //   setShowModal(!showModal);
  //   if (message) {
  //     setInfo(message);
  //   }
  // };
  useEffect(() => {
    // const encryptLoginCredentials = async (payload: any) => {
    //   const url = `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/legacy/encryptPayload`;
    //   const result = await axios.post(url, {
    //     call: 'encryptPayload',
    //     payload,
    //     businessid: 995,
    //     apiKey: 'd8810712-df40-11e4-9ccc-22000ac88c3a',
    //   });
    //   return result.data;
    // };

    const decryptLoginCredentials = async (
      ctxt: string,
      iv: string,
      sig: string,
      isodate: string
    ) => {
      const url = `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/legacy/decryptPayload`;
      const result = await axios.post(url, {
        call: 'decryptPayload',
        ctxt,
        iv,
        sig,
        isodate,
        businessid: 995,
        apiKey: 'd8810712-df40-11e4-9ccc-22000ac88c3a',
      });
      return result.data;
    };

    sessionStorage.removeItem('logout');
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const ctxtParam = urlParams.get('ctxt');
      const ivParam = urlParams.get('iv');
      const sigParam = urlParams.get('sig');
      const isodateParam = urlParams.get('siodate');
      if (ctxtParam && ivParam && sigParam && isodateParam) {
        decryptLoginCredentials(
          ctxtParam,
          ivParam,
          sigParam,
          isodateParam
        ).then((res) => {
          const cred = JSON.parse(res ?? '{}');
          // console.log('cred', cred);
          if (cred?.u && cred?.p) {
            formik.setFieldValue('email', cred.u);
            formik.setFieldValue('password', cred.p);
            setTimeout(() => {
              formik.handleSubmit();
            }, 3000);
          }
        });
      }
      // encryptLoginCredentials({
      //   u: 'aliu2@kixie.com',
      //   p: 'welcome1',
      // }).then((re) => {
      //   console.log({ re });
      // decryptLoginCredentials(re.ctxt, re.iv, re.sig, re.isodate).then(
      //   (re1) => {
      //     const cred = JSON.parse(re1 ?? '{}');
      //     // console.log('cred', cred);
      //     if (cred?.u && cred?.p) {
      //       formik.setFieldValue('email', cred.u);
      //       formik.setFieldValue('password', cred.p);
      //       setTimeout(() => {
      //         formik.handleSubmit();
      //       }, 3500);
      //     }
      //   }
      // );
      // });
    } catch (e) {
      // console.log('decrypt payload error', e)
    }
  }, []);

  return (
    <div
      className="login login-4 login-signin-on d-flex flex-row-fluid"
      id="kt_login_signin_form"
    >
      <div
        style={{
          // remove all inline styles, replace with makeStyles
          position: 'absolute',
          left: '25px',
          top: '10px',
        }}
      >
        <img
          style={{
            height: '74px',
            width: '182px',
          }}
          src={kixieLogo}
        />
      </div>
      <div
        className="d-flex flex-center flex-row-fluid bgi-size-cover bgi-position-top bgi-no-repeat"
        style={{ backgroundImage: 'url(/media/bg/bg-3.jpg)' }}
      >
        <div className="login-form p-7 position-relative overflow-hidden">
          {/* begin::Head */}
          <div className="d-flex flex-column flex-center mb-15">
            <h3 className="font-size-h1">
              <FormattedMessage id="AUTH.LOGIN.TITLE" />
            </h3>
            <p className="text-muted font-weight-bold">
              Enter your details to login to your account
            </p>
          </div>
          {/* end::Head */}

          {/* begin::Form */}
          <div className="login-signin">
            <form
              onSubmit={formik.handleSubmit}
              className="form fv-plugins-bootstrap fv-plugins-framework"
            >
              {formik.status && (
                <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                  <div className="alert-text font-weight-bold">
                    {formik.status}
                  </div>
                </div>
              )}

              {info && (
                <div className="mb-10 alert alert-custom alert-light-info alert-dismissible">
                  <div className="alert-text font-weight-bold">{info}</div>
                </div>
              )}

              <div className="form-group fv-plugins-icon-container">
                <input
                  // autoComplete="username"
                  placeholder="Email"
                  type="email"
                  className={`form-control h-auto form-control-solid py-4 px-8 ${getInputClasses(
                    'email'
                  )}`}
                  {...formik.getFieldProps('email')}
                  onChange={(e) => {
                    formik.handleChange(e); // Keep Formik's default behavior
                    if (formik.status) {
                      formik.setStatus(null); // Clear the status when the user types
                    }
                  }}
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">{formik.errors.email}</div>
                  </div>
                ) : null}
              </div>
              <div className="form-group fv-plugins-icon-container">
                <input
                  // autoComplete="current-password"
                  placeholder="Password"
                  type="password"
                  className={`form-control h-auto form-control-solid py-4 px-8 ${getInputClasses(
                    'password'
                  )}`}
                  {...formik.getFieldProps('password')}
                  onChange={(e) => {
                    formik.handleChange(e); // Keep Formik's default behavior
                    if (formik.status) {
                      formik.setStatus(null); // Clear the status when the user types
                    }
                  }}
                />
                {formik.touched.password && formik.errors.password ? (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      {formik.errors.password}
                    </div>
                  </div>
                ) : null}
              </div>
              <div>
                {captchaRequired ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      padding: '28px',
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.VITE_APP_RECAPTCHA_KEY!}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
                <label
                  style={{
                    color: '#0098C7',
                    cursor: 'pointer',
                  }}
                  id="kt_login_forgot"
                  onClick={() => handleShow()}
                >
                  <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                </label>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={formik.isSubmitting}
                  disableElevation
                >
                  Sign In
                  {formik.isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
                <small
                  style={{
                    paddingLeft: '10px',
                    paddingTop: '5px',
                  }}
                  className="tiny"
                >
                  {'   '}
                  By logging into Kixie PowerCall, you are agreeing to the
                  Kixie.com
                  <a
                    style={{ marginLeft: '5px' }}
                    href="https://www.kixie.com/terms/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Terms of Use
                  </a>
                </small>
              </div>
            </form>
          </div>
          {/* end::Form */}
          <ForgetPasswordModal show={show} onHide={handleClose} />
        </div>
      </div>
    </div>
  );
}

export default injectIntl(Login);
